import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  Typography,
} from '@mui/material';

const DropoffDetailDialog = ({ open, setOpen }) => {
  const handleClose = () => {
    setOpen((prev) => !prev);
  };

  const dropoffSections = useMemo(() => {
    return [
      {
        id: 'intro',
        type: 'intro',
        text: (
          <>
            <Typography variant='body1' component='p' gutterBottom>
              Please drop off your sponsored Angel wishes at the location you
              selected in your application. You will need to present your QR
              code or confirmation number at the time of drop-off. You can find
              your QR code or confirmation number in the confirmation email that
              you should have received after submitting your application.
            </Typography>

            <Typography variant='body1' component='p' gutterBottom>
              To make any changes to your drop-off location, don't hesitate to
              contact us directly at{' '}
              <Link color='primary' href='mailto:angeltree@ufvsus.ca'>
                angeltree@ufvsus.ca
              </Link>{' '}
              or call{' '}
              <Link color='primary' href='tel:6048644613'>
                604-864-4613
              </Link>
              .
            </Typography>
          </>
        ),
      },
      {
        id: 'abbotsford',
        title: 'Abbotsford Drop-off',
        location: {
          label: 'Student Union Society Office, Building S, Room S1109',
          href: 'https://www.ufv.ca/maps/abbotsford-campus/',
        },
        dates: 'Nov 18 - Dec 3',
        days: 'Monday to Friday',
        hours: '9:00 am to 4:00 pm',
        evening: {},
        extendedDateTime: {
          dates: 'Thursday, November 26, 2026',
          hours: '4:00 pm to 8:00 pm',
        }
      },
      {
        id: 'chilliwack',
        title: 'Chilliwack Drop-off',
        location: {
          label: 'CEP Campus, Student Services, Building A, Room A1318',
          href: 'https://www.ufv.ca/maps/chilliwack-campus/',
        },
        dates: 'Nov 18 - Dec 3',
        days: 'Monday to Friday',
        hours: 'TBC',
        evening: {},
        extendedDateTime: {
          dates: 'Thursday, November 26, 2026',
          hours: '4:00 pm to 8:00 pm',
        }
      },
    ];
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>Drop-Off Details</DialogTitle>
      <DialogContent>
        <DialogContentText component='div'>
          {/* Intro */}
          {dropoffSections.map((section) => {
            if (section.type === 'intro') {
              return (
                <Box key={section.id}>
                  {section.text}
                  <Divider sx={{ marginTop: 2 }} />
                </Box>
              );
            }
            // Drop-off locations
            return (
              <Box key={section.id} sx={{ marginTop: 2 }}>
                <Typography variant='subtitle1' component='p' fontWeight='bold'>
                  {section.title}
                </Typography>

                <Typography variant='body2' component='p'>
                  <span style={{ fontWeight: 'bold' }}>Location:</span>{' '}
                  <Link color='primary' href={section.location.href}>
                    {section.location.label}
                  </Link>
                </Typography>

                <Typography variant='body2' component='p'>
                  <span style={{ fontWeight: 'bold' }}>Dates:</span>{' '}
                  {section.dates}
                </Typography>

                <Typography variant='body2' component='p'>
                  <span style={{ fontWeight: 'bold' }}>Days:</span>{' '}
                  {section.days}
                </Typography>

                <Typography variant='body2' component='p'>
                  <span style={{ fontWeight: 'bold' }}>Hours:</span>{' '}
                  {section.hours}
                </Typography>
                <Typography
                  variant='body2'
                  component='p'
                  sx={{ fontStyle: 'italic', mt: 0.5 }}
                >
                  *Closed on Weekends*
                </Typography>

                {Object.keys(section.evening).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant='subtitle2'
                      component='p'
                      fontWeight='bold'
                    >
                      Evening Drop-Off:
                    </Typography>
                    <Typography variant='body2' component='p'>
                      <span style={{ fontWeight: 'bold' }}>Location:</span>{' '}
                      <Link
                        color='primary'
                        href={section.evening?.href ?? section.location.href}
                      >
                        {section.evening?.label ?? section.location.label}
                      </Link>
                    </Typography>
                    <Typography variant='body2' component='p'>
                      <span style={{ fontWeight: 'bold' }}>Date:</span>{' '}
                      {section.evening?.date}
                    </Typography>
                    <Typography variant='body2' component='p'>
                      <span style={{ fontWeight: 'bold' }}>Hours:</span>{' '}
                      {section.evening?.hours}
                    </Typography>
                    <Typography
                      variant='body2'
                      component='p'
                      sx={{ fontStyle: 'italic', mt: 0.5 }}
                    >
                      *Closed on Weekends*
                    </Typography>
                  </Box>
                )}

                {Object.keys(section.extendedDateTime).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant='subtitle2'
                      component='p'
                      fontWeight='bold'
                    >
                      Extended Drop-Off:
                    </Typography>
                    <Typography variant='body2' component='p'>
                      <span style={{ fontWeight: 'bold' }}>Date:</span>{' '}
                      {section.extendedDateTime?.dates}
                    </Typography>
                    <Typography variant='body2' component='p'>
                      <span style={{ fontWeight: 'bold' }}>Hours:</span>{' '}
                      {section.extendedDateTime?.hours}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ marginTop: 2 }} />
              </Box>
            );
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={handleClose} autoFocus>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DropoffDetailDialog;
